import Vue from 'vue';
import Vuex, { GetterTree, MutationTree, ActionTree } from 'vuex';
import api from '@/assets/api';
import {
  PlanI, PostulanteI, validarTokenPostulante, EstudianteI, validarTokenEstudianteAntiguo,
  PostulanteEduContI, validarTokenEducacionContinua,
} from '@/assets/api/matricula';

Vue.use(Vuex);

// ===== ENUMS AND CONSTANTS =====
enum TiposUsuario {
  NULO = 0,
  POSTULANTE = 1,
  ESTUDIANTE = 2,
  EDUCONTINUA = 3,
}

const STORAGE_KEYS = {
  TOKEN: 'token',
  TIPO_USUARIO: 'tipo_usuario',
  ID_USUARIO: 'id_usuario',
} as const;

const COLOR_PLAN: Record<number, string> = {
  0: 'var(--azul-institucional)',
  1: 'var(--azul-enfermeria)',
  2: 'var(--rojo-obstetricia)',
  3: 'var(--verde-agronomia)',
  4: 'var(--verde-forestal)',
  5: 'var(--naranjo-industrial)',
  6: 'var(--amarillo-social)',
  7: 'var(--celeste-psicologia)',
  8: 'var(--rosado-informatica)',
  22: 'var(--azul-ing-comercial)',
  23: 'var(--verde-terapia-ocupacional)',
  31: 'var(--gris-derecho)',
  32: 'var(--morado-adm-publica)',
  33: 'var(--amarillo-ing-renovables)',
};

const PLANES_OSCUROS = [0, 1, 2, 4, 8, 22, 23,31,32];

// ===== INTERFACES =====
interface RootState {
  tipoUsuario: TiposUsuario;
  nombre: string;
  plan: PlanI;
  idEstudiante: number | null;
  etapaEstudiante: number;
  idPostulante: number | null;
  aceptaContrato: boolean;
  sesion: boolean;
}

interface LoginPostulantePayload {
  postulante: PostulanteI;
  accessToken: string;
}

interface LoginEstudiantePayload {
  estudiante: EstudianteI;
  accessToken: string;
}

interface LoginPostulanteEduContPayload {
  postulante: PostulanteEduContI;
  accessToken: string;
}

// ===== STATE =====
const state: RootState = {
  tipoUsuario: TiposUsuario.NULO,
  nombre: '',
  plan: {} as PlanI,
  idEstudiante: null,
  etapaEstudiante: 0,
  idPostulante: null,
  aceptaContrato: false,
  sesion: false,
};

// ===== GETTERS =====
const getters: GetterTree<RootState, RootState> = {
  // Tipo usuario
  esPostulante: (state): boolean => state.tipoUsuario === TiposUsuario.POSTULANTE,
  esEstudianteAntiguo: (state): boolean => state.tipoUsuario === TiposUsuario.ESTUDIANTE,
  esPostulanteEduCont: (state): boolean => state.tipoUsuario === TiposUsuario.EDUCONTINUA,

  // Tipo de programa
  esMagister: (state): boolean => state.plan?.carrera_tipo === 'Magíster',

  // Datos de estudiante
  idEstudiante: (state): number | null => state.idEstudiante,
  idPlan: (state): number => state.plan?.id || 0,
  nombre: (state): string => state.nombre,
  carrera: (state): string => state.plan?.carrera || '',
  aceptaContrato: (state): boolean => state.aceptaContrato,
  depto: (state): string => state.plan?.depto || '',
  etapaEstudiante: (state): number => state.etapaEstudiante,

  // Consultas de colores de carrera
  colorPlan: (state, getters): string => {
    const planId = getters.idPlan as number;
    return COLOR_PLAN[planId] || COLOR_PLAN[0] || 'var(--azul-institucional)';
  },

  esPlanOscuro: (state, getters): boolean => {
    const planId = getters.idPlan as number;
    return PLANES_OSCUROS.includes(planId);
  },

  colorTextoPlan: (state, getters): string => {
    const planId = getters.idPlan as number;

    if ([2, 3, 5, 6, 7, 8].includes(planId)) {
      return '#222';
    }
    if ([0, 1, 4].includes(planId)) {
      return 'var(--celeste-institucional)';
    }
    return 'var(--azul-institucional)';
  },

  // Páginas según tipo usuario
  linkInicio: (state, getters): string => {
    if (getters.esPostulanteEduCont) {
      return 'educontinua inicio';
    }
    return 'inicio';
  },

  linkBienvenida: (state, getters): string => {
    if (getters.esPostulante) {
      return 'bienvenida';
    }
    if (getters.esEstudianteAntiguo) {
      return 'inhabilitantes antiguos';
    }
    return 'educontinua bienvenida';
  },

  linkPagoMatricula: (): string => 'pago matricula',
  linkPagoArancel: (): string => 'pago arancel',
};

// ===== MUTATIONS =====
const mutations: MutationTree<RootState> = {
  loginPostulante(state, payload: LoginPostulantePayload): void {
    try {
      state.sesion = true;
      state.tipoUsuario = TiposUsuario.POSTULANTE;
      state.nombre = payload.postulante.nombre;
      state.plan = payload.postulante.plan;
      state.idEstudiante = payload.postulante.estudiante;
      state.etapaEstudiante = payload.postulante.etapa_estudiante;
      state.idPostulante = payload.postulante.id;

      // Set API authorization header
      api.defaults.headers.common.Authorization = `Bearer ${payload.accessToken}`;

      // Store in localStorage
      localStorage.setItem(STORAGE_KEYS.TOKEN, payload.accessToken);
      localStorage.setItem(STORAGE_KEYS.TIPO_USUARIO, TiposUsuario.POSTULANTE.toString());
      localStorage.setItem(STORAGE_KEYS.ID_USUARIO, payload.postulante.id.toString());
    } catch (error) {
      console.error('Error in loginPostulante mutation:', error);
      // Fallback to logout if localStorage fails
      mutations.logout(state);
    }
  },

  loginEstudiante(state, payload: LoginEstudiantePayload): void {
    try {
      state.sesion = true;
      state.tipoUsuario = TiposUsuario.ESTUDIANTE;
      state.nombre = payload.estudiante.nombre;
      state.aceptaContrato = payload.estudiante.acepta_contrato;
      state.plan = payload.estudiante.plan;
      state.idEstudiante = payload.estudiante.id;
      state.etapaEstudiante = payload.estudiante.etapa_estudiante;

      // Set API authorization header
      api.defaults.headers.common.Authorization = `Bearer ${payload.accessToken}`;

      // Store in localStorage
      localStorage.setItem(STORAGE_KEYS.TOKEN, payload.accessToken);
      localStorage.setItem(STORAGE_KEYS.TIPO_USUARIO, TiposUsuario.ESTUDIANTE.toString());
      localStorage.setItem(STORAGE_KEYS.ID_USUARIO, payload.estudiante.id.toString());
    } catch (error) {
      console.error('Error in loginEstudiante mutation:', error);
      // Fallback to logout if localStorage fails
      mutations.logout(state);
    }
  },

  loginPostulanteEduCont(state, payload: LoginPostulanteEduContPayload): void {
    try {
      state.sesion = true;
      state.tipoUsuario = TiposUsuario.EDUCONTINUA;
      state.nombre = payload.postulante.nombre;
      state.plan = payload.postulante.plan;
      state.idEstudiante = payload.postulante.estudiante;
      state.etapaEstudiante = payload.postulante.etapa_estudiante;
      state.idPostulante = payload.postulante.id;

      // Set API authorization header
      api.defaults.headers.common.Authorization = `Bearer ${payload.accessToken}`;

      // Store in localStorage
      localStorage.setItem(STORAGE_KEYS.TOKEN, payload.accessToken);
      localStorage.setItem(STORAGE_KEYS.TIPO_USUARIO, TiposUsuario.EDUCONTINUA.toString());
      localStorage.setItem(STORAGE_KEYS.ID_USUARIO, payload.postulante.id.toString());
    } catch (error) {
      console.error('Error in loginPostulanteEduCont mutation:', error);
      // Fallback to logout if localStorage fails
      mutations.logout(state);
    }
  },

  actualizarEstudiante(state, idEstudiante: number): void {
    state.idEstudiante = idEstudiante;
  },

  actualizarNombre(state, nombre: string): void {
    state.nombre = nombre;
  },

  actualizaAceptaContrato(state, aceptaContrato: boolean): void {
    state.aceptaContrato = aceptaContrato;
  },

  // Solo aumentar la etapa a medida que se avanza, si se retrocede a un paso no se retrocede la etapa
  actualizarEtapa(state, etapa: number): void {
    state.etapaEstudiante = Math.max(etapa, state.etapaEstudiante || 0);
  },

  logout(state): void {
    try {
      // Reset state
      state.sesion = false;
      state.tipoUsuario = TiposUsuario.NULO;
      state.nombre = '';
      state.plan = {} as PlanI;
      state.idEstudiante = null;
      state.etapaEstudiante = 0;
      state.idPostulante = null;
      state.aceptaContrato = false;

      // Clear API authorization header
      api.defaults.headers.common.Authorization = null;

      // Clear localStorage
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.TIPO_USUARIO);
      localStorage.removeItem(STORAGE_KEYS.ID_USUARIO);
    } catch (error) {
      console.error('Error in logout mutation:', error);
      // Even if localStorage fails, ensure state is reset
      state.sesion = false;
      state.tipoUsuario = TiposUsuario.NULO;
    }
  },
};

// ===== ACTIONS =====
const actions: ActionTree<RootState, RootState> = {
  async init({ commit }): Promise<void> {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      const tipoUsuarioStr = localStorage.getItem(STORAGE_KEYS.TIPO_USUARIO);
      const idUsuarioStr = localStorage.getItem(STORAGE_KEYS.ID_USUARIO);

      // If any required data is missing, logout
      if (!token || !tipoUsuarioStr || !idUsuarioStr) {
        commit('logout');
        return;
      }

      const tipoUsuario = parseInt(tipoUsuarioStr, 10);
      const idUsuario = parseInt(idUsuarioStr, 10);

      // Validate parsed values
      if (isNaN(tipoUsuario) || isNaN(idUsuario)) {
        commit('logout');
        return;
      }

      // Validate token based on user type
      switch (tipoUsuario) {
        case TiposUsuario.POSTULANTE: {
          const data = await validarTokenPostulante(idUsuario, token);
          commit('loginPostulante', data);
          break;
        }
        case TiposUsuario.ESTUDIANTE: {
          const data = await validarTokenEstudianteAntiguo(idUsuario, token);
          commit('loginEstudiante', data);
          break;
        }
        case TiposUsuario.EDUCONTINUA: {
          const data = await validarTokenEducacionContinua(idUsuario, token);
          commit('loginPostulanteEduCont', data);
          break;
        }
        default:
          // Unknown user type, logout
          commit('logout');
          break;
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      commit('logout');
    }
  },

  // Helper action for manual login
  async loginUser({ commit }, { tipo, id, token }: { tipo: TiposUsuario; id: number; token: string }): Promise<void> {
    try {
      switch (tipo) {
        case TiposUsuario.POSTULANTE: {
          const data = await validarTokenPostulante(id, token);
          commit('loginPostulante', data);
          break;
        }
        case TiposUsuario.ESTUDIANTE: {
          const data = await validarTokenEstudianteAntiguo(id, token);
          commit('loginEstudiante', data);
          break;
        }
        case TiposUsuario.EDUCONTINUA: {
          const data = await validarTokenEducacionContinua(id, token);
          commit('loginPostulanteEduCont', data);
          break;
        }
        default:
          throw new Error('Invalid user type');
      }
    } catch (error) {
      console.error('Login failed:', error);
      commit('logout');
      throw error;
    }
  },
};

// ===== STORE CONFIGURATION =====
const store = new Vuex.Store<RootState>({
  //strict: process.env.NODE_ENV !== 'production',
  state,
  getters,
  mutations,
  actions,
});

// ===== EXPORTS =====
export default store;

// Export types for use in components
export { RootState, TiposUsuario };
export type { LoginPostulantePayload, LoginEstudiantePayload, LoginPostulanteEduContPayload };
